import { Box3, BoxGeometry, Group, Mesh, MeshBasicMaterial, type Object3D, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { createRackSelectionHighlight } from "./rack-shelf";

export const palletReciprocatingLiftLayerHeight = 2;
const selectionHighlightPadding = 0.06;

export type PalletReciprocatingLiftOptions = {
  bottomSideRailInset?: number;
  layerCount?: number;
  position?: { x?: number; y?: number; z?: number };
  rotationY?: number;
};

type BottomAlignedPart = {
  content: Group;
  object: Group;
  size: Vector3;
};

const defaultOptions = {
  bottomSideRailInset: 0.25,
  layerCount: 4,
  rotationY: 0
};

export class PalletReciprocatingLift extends Group {
  private bottomSideRail?: BottomAlignedPart;
  private bottomSideRailInset: number;
  private readonly hitTarget = createHitTarget();
  private layerCount: number;
  private readonly structure = new Group();
  private layerBeamTemplate?: Group;
  private layerBeams: Group[] = [];
  private movingAssembly?: BottomAlignedPart;
  private post?: BottomAlignedPart;
  private selected = false;
  private readonly selectionHighlight = createRackSelectionHighlight();
  private readonly selectionSize = new Vector3();
  private topHeight = 0;
  private top?: Group;

  constructor({
    bottomSideRailInset = defaultOptions.bottomSideRailInset,
    layerCount = defaultOptions.layerCount,
    position = {},
    rotationY = defaultOptions.rotationY
  }: PalletReciprocatingLiftOptions = {}) {
    super();
    this.bottomSideRailInset = validateNonNegativeNumber(
      bottomSideRailInset,
      "bottomSideRailInset"
    );
    this.layerCount = validateLayerCount(layerCount);
    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.name = "pallet-reciprocating-lift";
    this.structure.name = "pallet-reciprocating-lift-structure";
    this.hitTarget.name = "pallet-reciprocating-lift-hit-target";
    this.selectionHighlight.name = "pallet-reciprocating-lift-selection-highlight";
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
    this.add(this.structure, this.hitTarget, this.selectionHighlight);
  }

  static async create(
    options: PalletReciprocatingLiftOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const lift = new PalletReciprocatingLift(options);
    await lift.loadStructure(loader);
    return lift;
  }

  getLayerCount() {
    return this.layerCount;
  }

  getHitTargets(): Object3D[] {
    return [this.hitTarget];
  }

  setBottomSideRailInset(bottomSideRailInset: number) {
    this.bottomSideRailInset = validateNonNegativeNumber(
      bottomSideRailInset,
      "bottomSideRailInset"
    );
    this.layoutStructure();
  }

  getTotalHeight() {
    return this.layerCount * palletReciprocatingLiftLayerHeight;
  }

  setLayerCount(layerCount: number) {
    this.layerCount = validateLayerCount(layerCount);
    this.layoutStructure();
  }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.selectionHighlight.visible = selected;
  }

  private async loadStructure(loader: ModelAssetLoader) {
    const [base, bottomSideRail, post, top, layerBeam, movingAssembly] = await Promise.all([
      loadBottomAlignedPart(
        loader,
        ModelAsset.PalletReciprocatingLiftBase,
        "pallet-reciprocating-lift-base"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.PalletReciprocatingLiftBottomSideRail,
        "pallet-reciprocating-lift-bottom-side-rail"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.PalletReciprocatingLiftPost,
        "pallet-reciprocating-lift-post"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.PalletReciprocatingLiftTop,
        "pallet-reciprocating-lift-top"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.PalletReciprocatingLiftLayerBeam,
        "pallet-reciprocating-lift-layer-beam-template"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.PalletReciprocatingLiftMovingAssembly,
        "pallet-reciprocating-lift-moving-assembly"
      )
    ]);

    this.bottomSideRail = bottomSideRail;
    this.movingAssembly = movingAssembly;
    this.post = post;
    this.top = top.object;
    this.topHeight = top.size.y;
    this.layerBeamTemplate = layerBeam.object;
    this.selectionSize.set(
      Math.max(
        base.size.x,
        bottomSideRail.size.x,
        post.size.x,
        top.size.x,
        layerBeam.size.x,
        movingAssembly.size.x
      ),
      0,
      Math.max(
        base.size.z,
        bottomSideRail.size.z,
        post.size.z,
        top.size.z,
        layerBeam.size.z,
        movingAssembly.size.z
      )
    );
    this.structure.add(
      base.object,
      bottomSideRail.object,
      post.object,
      top.object,
      movingAssembly.object
    );
    this.layoutStructure();
  }

  private layoutStructure() {
    if (
      !this.bottomSideRail ||
      !this.post ||
      !this.top ||
      !this.layerBeamTemplate ||
      !this.movingAssembly
    ) {
      return;
    }

    const totalHeight = this.getTotalHeight();
    this.bottomSideRail.object.position.x =
      -this.post.size.x / 2 + this.bottomSideRail.size.x / 2 + this.bottomSideRailInset;
    this.post.content.scale.y = totalHeight / this.post.size.y;
    this.top.position.y = totalHeight;
    this.movingAssembly.object.position.y =
      palletReciprocatingLiftLayerHeight +
      (palletReciprocatingLiftLayerHeight - this.movingAssembly.size.y) / 2;
    this.movingAssembly.object.visible = this.layerCount >= 2;

    this.layerBeams.forEach((layerBeam) => {
      this.structure.remove(layerBeam);
    });
    this.layerBeams = Array.from({ length: this.layerCount + 1 }, (_, index) => {
      const layerBeam = this.layerBeamTemplate?.clone(true) ?? new Group();
      layerBeam.name = `pallet-reciprocating-lift-layer-beam-${index}`;
      layerBeam.position.y = index * palletReciprocatingLiftLayerHeight;
      if (index === this.layerCount) {
        layerBeam.rotation.z = Math.PI;
      }
      return layerBeam;
    });
    this.structure.add(...this.layerBeams);
    this.updateSelectionHighlight(totalHeight);
  }

  private updateSelectionHighlight(totalHeight: number) {
    const outlineHeight = totalHeight + this.topHeight;
    this.hitTarget.position.y = outlineHeight / 2;
    this.hitTarget.scale.set(this.selectionSize.x, outlineHeight, this.selectionSize.z);
    this.selectionHighlight.position.y = outlineHeight / 2;
    this.selectionHighlight.scale.set(
      this.selectionSize.x + selectionHighlightPadding * 2,
      outlineHeight + selectionHighlightPadding * 2,
      this.selectionSize.z + selectionHighlightPadding * 2
    );
    this.selectionHighlight.visible = this.selected;
  }
}

function createHitTarget() {
  const material = new MeshBasicMaterial({
    depthWrite: false,
    opacity: 0,
    transparent: true
  });
  material.colorWrite = false;
  return new Mesh(new BoxGeometry(1, 1, 1), material);
}

async function loadBottomAlignedPart(
  loader: ModelAssetLoader,
  asset: ModelAsset,
  name: string
): Promise<BottomAlignedPart> {
  const model = await loader.load(asset);
  model.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(model);

  if (bounds.isEmpty()) {
    throw new Error(`${asset} model has no renderable geometry`);
  }

  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const object = new Group();
  const content = new Group();

  model.position.set(
    model.position.x - center.x,
    model.position.y - bounds.min.y,
    model.position.z - center.z
  );
  object.name = name;
  content.add(model);
  object.add(content);
  return { content, object, size };
}

function validateLayerCount(value: number) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError("layerCount must be a positive integer");
  }
  return value;
}

function validateNonNegativeNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative finite number`);
  }
  return value;
}
